import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const fetchCartOrWishlist = createAsyncThunk(
  "cart/fetchCartOrWishlist",
  async ({ type = "all" } = {}, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;

      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found in token.");

      const url =
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/CartAndWishlist/GetProductsFromCartOrWishlist" +
        `?userId=${encodeURIComponent(userId)}&type=${encodeURIComponent(
          type
        )}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          // If your backend expects Bearer, change to: Authorization: `Bearer ${token}`
          Authorization: token,
        },
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch {}
        return rejectWithValue(msg);
      }

      const data = await res.json().catch(() => ({}));
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load cart");
    }
  }
);

/**
 * POST add/update cart quantity using same API
 * POST .../AddProductToCartOrWishlist
 */
export const addToCartOrWishlist = createAsyncThunk(
  "cart/addToCartOrWishlist",
  async ({ type, productId, quantity }, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;

      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found in token.");

      const payload = {
        type, // "cart" | "wishlist"
        userId,
        productId,
        ...(type === "cart" ? { quantity: quantity ?? 1 } : {}),
      };

      const res = await fetch(
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/CartAndWishlist/AddProductToCartOrWishlist",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // If backend expects Bearer, change to: Authorization: `Bearer ${token}`
            Authorization: token,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch {}
        return rejectWithValue(msg);
      }

      const data = await res.json().catch(() => ({}));
      return { data, type, productId, quantity };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update cart");
    }
  }
);

export const deleteFromCartOrWishlist = createAsyncThunk(
  "cart/deleteFromCartOrWishlist",
  async ({ type = "cart", productId }, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;

      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found in token.");
      if (!productId) return rejectWithValue("ProductId is required.");

      const res = await fetch(
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/CartAndWishlist/DeleteFromCartOrWishlist",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token, // if Bearer: `Bearer ${token}`
          },
          body: JSON.stringify({
            type,
            productId,
            userId,
          }),
        }
      );

      // safe parse
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }

      if (!res.ok) {
        const msg =
          data?.message || data?.error || `Request failed (${res.status})`;
        return rejectWithValue(msg);
      }

      return { type, productId };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to remove item");
    }
  }
);

const initialState = {
  items: [], // normalized cart items
  status: "idle", // idle | loading | succeeded | failed
  error: "",
  updatingByProductId: {}, // per-item spinner
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearCartError: (state) => {
      state.error = "";
    },
    // optimistic update for qty
    setLocalQty: (state, action) => {
      const { productId, quantity } = action.payload;
      const idx = state.items.findIndex((x) => x.productId === productId);
      if (idx >= 0) {
        state.items[idx].quantity = quantity;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartOrWishlist.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchCartOrWishlist.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = "";

        const payload = action.payload || {};
        const d = payload.data || payload;

        // ✅ Your API shape:
        // d.cart = { count, items: [...] }
        // d.wishlist = { count, items: [...] }

        // If type=all, show cart items by default (your Cart page should show cart)
        // If you later make Wishlist page, use d.wishlist.items there.
        const cartItemsRaw = d?.cart?.items;
        const wishlistItemsRaw = d?.wishlist?.items;

        // Prefer cart for Cart page
        const rawArr = Array.isArray(cartItemsRaw)
          ? cartItemsRaw
          : Array.isArray(d?.items)
          ? d.items
          : [];

        state.items = rawArr.map((it) => {
          // your item has productDetails nested
          const p = it?.productDetails || it?.product || it;

          return {
            productId: it?.productId || p?.productId || p?.id || it?.id,
            name: p?.name || p?.title || "Product",
            category: p?.category || p?.categoryName || "category",
            images: p?.images || [],
            offeredPrice: p?.offeredPrice ?? p?.price ?? 0,
            originalPrice: p?.originalPrice ?? null,
            quantity: it?.quantity ?? 1,
          };
        });
      })

      .addCase(fetchCartOrWishlist.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to load cart";
        state.items = [];
      })

      .addCase(addToCartOrWishlist.pending, (state, action) => {
        const { productId } = action.meta.arg;
        state.updatingByProductId[productId] = true;
        state.error = "";
      })
      .addCase(addToCartOrWishlist.fulfilled, (state, action) => {
        const { productId, quantity, type } = action.payload;
        state.updatingByProductId[productId] = false;

        // If it's cart update and quantity becomes 0 -> remove locally
        if (type === "cart" && typeof quantity === "number" && quantity <= 0) {
          state.items = state.items.filter((x) => x.productId !== productId);
        }
      })
      .addCase(addToCartOrWishlist.rejected, (state, action) => {
        const { productId } = action.meta.arg || {};
        if (productId) state.updatingByProductId[productId] = false;
        state.error = action.payload || "Cart update failed";
      })

      .addCase(deleteFromCartOrWishlist.pending, (state, action) => {
        const { productId } = action.meta.arg;
        state.updatingByProductId[productId] = true;
        state.error = "";
      })
      .addCase(deleteFromCartOrWishlist.fulfilled, (state, action) => {
        const { productId } = action.payload;
        state.updatingByProductId[productId] = false;

        // remove locally
        state.items = state.items.filter((x) => x.productId !== productId);
      })
      .addCase(deleteFromCartOrWishlist.rejected, (state, action) => {
        const { productId } = action.meta.arg || {};
        if (productId) state.updatingByProductId[productId] = false;
        state.error = action.payload || "Remove failed";
      });
  },
});

export const { clearCartError, setLocalQty } = cartSlice.actions;
export default cartSlice.reducer;

export const selectCartItems = (s) => s.cart.items;
export const selectCartStatus = (s) => s.cart.status;
export const selectCartError = (s) => s.cart.error;
export const selectUpdatingByProductId = (s) => s.cart.updatingByProductId;
